/**
 * bridge/calendar_event_controller.js — "calendar-event" bridge component
 * (Stimulus + Strada).
 *
 * Custom-tier bridge component: adds a scheduled job to the device calendar
 * (title, notes, start/end, location). Stimulus identifier
 * "bridge--calendar-event" (glob-derived from this subdir); Strada component
 * name "calendar-event" MUST match the iOS class var name
 * (native/ios/Chippie/Bridge/CalendarEventComponent.swift).
 *
 * Why it extends Strada's BridgeComponent: the iOS calendar editor is reached
 * via this.send("add", ...), which only works on a connected bridge component
 * inside the iOS Hotwire Native shell. Extending BridgeComponent gives us that
 * send() transport AND the UA-gated connect() (it only connects in a native
 * shell).
 *
 * Three-way transport: rather than each caller branching on platform, this
 * controller registers a single calendarEvent() impl set onto the
 * window.ChippieBridge facade ({ ios, android, pwa }) on connect. The facade
 * (chippie-bridge.js) owns platform detection and picks the right impl per call.
 * Callers use:
 *   window.ChippieBridge.calendarEvent({ title, notes, startAt, endAt, location })
 * where startAt/endAt are ISO 8601 strings. Returns a Promise resolving to
 * { saved } on iOS; android/pwa resolve once the intent/download is handed off
 * (no save confirmation is observable on those platforms).
 *   - ios:     this.send("add", data, msg) over Strada to
 *              CalendarEventComponent.swift; resolves { saved: msg.data.saved }.
 *   - android: window.ChippieNative.addCalendarEvent(title, notes, startMs,
 *              endMs, location) (@JavascriptInterface) — fires an
 *              ACTION_INSERT CalendarContract.Events intent. Epoch millis.
 *   - pwa:     downloads an .ics file (RFC 5545 VEVENT) which the OS opens in the
 *              default calendar app — the cross-browser "add to calendar" path.
 *
 * Callers use window.ChippieBridge.calendarEvent(detail) directly, OR wire a
 * button with data-action="bridge--calendar-event#add" and the values below.
 *
 * Template wiring:
 *   <button data-controller="bridge--calendar-event"
 *           data-bridge--calendar-event-title-value="Smith fence repair"
 *           data-bridge--calendar-event-notes-value="Bring the post-hole digger"
 *           data-bridge--calendar-event-start-at-value="2026-07-01T09:00:00+10:00"
 *           data-bridge--calendar-event-end-at-value="2026-07-01T11:00:00+10:00"
 *           data-bridge--calendar-event-location-value="12 Bourke St, Melbourne"
 *           data-action="bridge--calendar-event#add">Add to calendar</button>
 *
 * The Android + PWA impls carry no per-instance state, so they are registered at
 * module load via registerStandalone() and ChippieBridge.calendarEvent() resolves
 * everywhere. Only the ios impl needs a live Strada instance for this.send, so
 * connect() overrides it and disconnect() reverts to the placeholder. Registering
 * solely on connect (as this file used to) left ChippieBridge.calendarEvent
 * UNDEFINED on PWA and desktop, where the UA-gated BridgeComponent.connect()
 * never fires — so the .ics download path, the only one that works in a browser,
 * was unreachable and callers got a TypeError.
 *
 * Web side follows the Chippie sibling pattern (share/location controllers); no
 * canonical Masilotti calendar component exists, so the EventKit/CalendarContract
 * transport is Chippie-original.
 */

import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Normalise to a flat { title, notes, startAt, endAt, location }.
function payload(data) {
  data = data || {};
  return {
    title: data.title || "",
    notes: data.notes || "",
    startAt: data.startAt || "",
    endAt: data.endAt || "",
    location: data.location || "",
  };
}

function toMillis(iso) {
  if (!iso) return Date.now();
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? Date.now() : ms;
}

function icsDate(iso) {
  if (!iso) return "";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  return new Date(ms).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

function icsEscape(text) {
  return (text || "")
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

// Minimal RFC 5545 VEVENT. ICS uses UTC basic-format timestamps
// (YYYYMMDDTHHMMSSZ). Newlines/commas in text fields are escaped per spec.
function buildIcs(p) {
  const start = icsDate(p.startAt);
  const end = icsDate(p.endAt) || icsDate(new Date(toMillis(p.startAt) + 3600000).toISOString());
  const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}@chippieapp.com`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Chippie//Calendar//EN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${icsDate(new Date().toISOString())}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${icsEscape(p.title)}`,
    `DESCRIPTION:${icsEscape(p.notes)}`,
    `LOCATION:${icsEscape(p.location)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return lines.join("\r\n");
}

// Android: ACTION_INSERT intent takes epoch millis; convert ISO strings.
function androidAdd(data) {
  const p = payload(data);
  const startMs = toMillis(p.startAt);
  const endMs = toMillis(p.endAt) || startMs + 3600000;
  window.ChippieNative.addCalendarEvent(p.title, p.notes, startMs, endMs, p.location);
  return Promise.resolve({ saved: null });
}

// PWA: build an .ics VEVENT and trigger a download; the OS hands it to the
// default calendar app. No save confirmation is observable.
function pwaAdd(data) {
  const p = payload(data);
  const blob = new Blob([buildIcs(p)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${(p.title || "event").replace(/[^\w-]+/g, "_")}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return Promise.resolve({ saved: null });
}

function registerStandalone() {
  if (!window.ChippieBridge || !window.ChippieBridge.register) return;
  window.ChippieBridge.register("calendarEvent", {
    // ios is overridden by the controller's connect() with a real send; this
    // placeholder resolves unsaved rather than hanging if selected before connect.
    ios: () => Promise.resolve({ saved: null }),
    android: androidAdd,
    pwa: pwaAdd,
  });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
  registerStandalone();
} else {
  document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
  static component = "calendar-event";

  static values = {
    title: String,
    notes: String,
    startAt: String,
    endAt: String,
    location: String,
  };

  connect() {
    super.connect();
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("calendarEvent", {
      ios: (data) =>
        new Promise((resolve) => {
          this.send("add", payload(data), (message) => {
            resolve({ saved: !!(message.data && message.data.saved) });
          });
        }),
      android: androidAdd,
      pwa: pwaAdd,
    });
  }

  disconnect() {
    // Revert the live-send ios impl to the placeholder instead of deleting the
    // key — deleting also took the .ics path away from Android and PWA, which
    // never needed this instance.
    registerStandalone();
    super.disconnect();
  }

  // data-action="bridge--calendar-event#add" trigger — add this element's values.
  add(event) {
    if (event) event.preventDefault();
    window.ChippieBridge.calendarEvent({
      title: this.titleValue,
      notes: this.notesValue,
      startAt: this.startAtValue,
      endAt: this.endAtValue,
      location: this.locationValue,
    });
  }
}
